import { Button, Flex, Tabs, Typography } from '@percent/lemonade'
import { useTranslation } from 'react-i18next'
import { useNavigate, useParams } from 'react-router-dom'
import { useEffect } from 'react'
import { GrantDetailsHeaderKey, GrantDetailsHeaderProps, GrantDetailsHeaderTabs } from './GrantDetailsHeader.types'

import styles from './GrantDetailsHeader.module.scss'

export const GrantDetailsHeader = ({
  programName,
  activeTab,
  setActiveTab,
  tasksOpen,
  setTasksOpen,
  hasTasks,
  hasNominations,
  hasAward
}: Readonly<GrantDetailsHeaderProps>) => {
  const { candidateId, tab } = useParams<{ candidateId: string; tab?: string }>()
  const { t } = useTranslation()
  const navigate = useNavigate()

  const handleTabChange = (newTab: GrantDetailsHeaderKey) => {
    navigate(`/grants/${candidateId}/${newTab}`)
    setActiveTab(newTab)
  }

  useEffect(() => {
    if (!tab || !Object.values(GrantDetailsHeaderKey).includes(tab as GrantDetailsHeaderKey)) {
      return
    }

    const nextTab = tab as GrantDetailsHeaderKey
    const isHiddenTab =
      (nextTab === GrantDetailsHeaderKey.NOMINATIONS && !hasNominations) ||
      (nextTab === GrantDetailsHeaderKey.DONATION && !hasAward)

    if (isHiddenTab) {
      setActiveTab(GrantDetailsHeaderKey.OVERVIEW)
      return
    }

    setActiveTab(nextTab)
  }, [hasAward, hasNominations, setActiveTab, tab])

  const tabs: GrantDetailsHeaderTabs = [
    {
      label: t('grants.grantDetails.header.overview'),
      key: GrantDetailsHeaderKey.OVERVIEW
    },
    ...(hasNominations
      ? [
          {
            label: t('grants.grantDetails.header.nominations'),
            key: GrantDetailsHeaderKey.NOMINATIONS
          } as const
        ]
      : []),
    {
      label: t('grants.grantDetails.header.organisation'),
      key: GrantDetailsHeaderKey.ORGANISATION
    },
    {
      label: t('grants.grantDetails.header.documents'),
      key: GrantDetailsHeaderKey.DOCUMENTS
    },
    ...(hasAward
      ? [
          {
            label: t('grants.grantDetails.header.donationDetails'),
            key: GrantDetailsHeaderKey.DONATION
          } as const
        ]
      : [])
  ]
  return (
    <Flex direction="column" className={styles.header} data-testid="grant-details-header">
      <Flex p={16} pb={8} gap={8} align="center">
        <Typography variant="h3" data-testid="program-name">
          {programName}
        </Typography>
        {!tasksOpen && hasTasks && (
          <Flex ml="auto">
            <Button variant="secondary" size="small" onClick={() => setTasksOpen(true)} data-testid="open-tasks-button">
              {t('grants.grantDetails.candidateTasks.openTasks')}
            </Button>
          </Flex>
        )}
      </Flex>

      <Tabs
        items={tabs}
        activeTab={activeTab}
        onChangeTab={newTab => handleTabChange(newTab as GrantDetailsHeaderKey)}
        scrollable
      />
    </Flex>
  )
}
